import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { DimensionInfo, MissingDimensionInfo } from "@rebuild/shared";
import { useState, useEffect } from "react";

interface FixMissingDimensionsModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (updatedData: DimensionInfo[]) => void;
  missingDimensions: MissingDimensionInfo[];
}

export default function FixMissingDimensionsModal({ open, onClose, onContinue, missingDimensions }: FixMissingDimensionsModalProps) {
  const [dimensionsData, setDimensionsData] = useState<DimensionInfo[]>(missingDimensions.map((item) => ({ ...item, quantity: 0 })));

  useEffect(() => {
    setDimensionsData(missingDimensions.map((item) => ({ ...item, quantity: 0 })));
  }, [missingDimensions]);

  const updateDimensionField = (index: number, field: keyof DimensionInfo, value: string) => {
    setDimensionsData((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const handleContinue = () => {
    onContinue(dimensionsData);
  };

  const hasEmptyFields = () => {
    return dimensionsData.some((item) => !item.quantity);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90%] w-full max-w-4xl flex-col overflow-y-auto rounded-lg !bg-white p-6 shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-center text-base leading-4 font-medium">Missing Dimension Information</DialogTitle>
        </DialogHeader>

        <div className="flex w-full flex-col space-y-6">
          <div className="space-y-6">
            <h3 className="text-sm font-medium">We need dimension information for these items.</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="p-2 text-left text-sm font-medium">Room</th>
                    <th className="p-2 text-left text-sm font-medium">Dimension</th>
                    <th className="p-2 text-left text-sm font-medium">Unit</th>
                    <th className="p-2 text-left text-sm font-medium">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {dimensionsData.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-2 text-sm">{item.roomName}</td>
                      <td className="p-2 text-sm">{`${item.dimension} (${item.acronym.toUpperCase()})`}</td>
                      <td className="p-2 text-sm">{item.unit}</td>
                      <td className="p-2">
                        <div className="w-24">
                          <Input
                            value={item.quantity}
                            onChange={(e) => updateDimensionField(index, "quantity", e.target.value)}
                            placeholder="Enter quantity"
                            type="number"
                            className="w-full"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 flex w-full flex-row justify-between gap-2 self-end">
            <Button className="!focus-visible:ring-0 !focus:outline-none !px-4 text-[#000]" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={hasEmptyFields()} className="!bg-[#000] !px-4 text-white" onClick={handleContinue}>
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
